import { memo, useState } from "react";

import CalendarIcon from "@uui/Calendar";
import { first, isEmpty, partition } from "lodash/fp";

import AnnotatedText from "@app/analytics/components/AnnotatedText";
import PlayClip from "@app/analytics/components/PlayClip";
import VideoDialog from "@app/analytics/components/VideoDialog";
import useRecordingStatus from "@app/analytics/hooks/recordings/useRecordingStatus";
import Thumbnail from "@app/analytics/MeetingReports/components/Thumbnail";
import type { VideoClip } from "@app/analytics/types";
import type {
  SearchResult as ISearchResult,
  SearchResultMatch,
} from "@app/analytics/types";
import ROUTES from "@app/common/routes";
import type {
  UnauthorizedVideoRecording,
  VideoRecording,
} from "@app/common/types";
import { pluralizePhrase, withQueryParamsUrl } from "@app/common/utils";

import Link from "@rui/Link";
import { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

import {
  MeetingTitle,
  Result,
  ResultMatchText,
  SubText,
  Wrapper,
} from "./styles";
import {
  MAX_VISIBLE_MATCHES,
  getClip,
  getMatchChunks,
  getMatchPrefixAndSuffix,
} from "./utils";

export interface SearchResultProps {
  query: string;
  recordingInfo?: {
    isLoading: boolean;
    recordingInfo?: VideoRecording | UnauthorizedVideoRecording;
  };
  result: ISearchResult;
}

const TextWithMatch = ({ match }: { match: SearchResultMatch }) => {
  const chunks = getMatchChunks(match.text);
  return (
    <>
      {chunks.map((chunk) =>
        chunk.isMatch ? (
          <AnnotatedText
            key={chunk.key}
            highlighted
            sx={{ fontWeight: 600, color: "inherit", px: 0, border: "none" }}
          >
            {chunk.value}
          </AnnotatedText>
        ) : (
          chunk.value
        )
      )}
    </>
  );
};

const Match = ({
  match,
  query,
  recordingInfo,
  sessionId,
}: {
  query: string;
  match: SearchResultMatch;
  recordingInfo: SearchResultProps["recordingInfo"];
  sessionId: string;
}) => {
  const [videoClip, setVideoClip] = useState<VideoClip | null>(null);

  const { status } = useRecordingStatus(recordingInfo);
  const clip =
    status === "available"
      ? getClip(match, recordingInfo?.recordingInfo)
      : undefined;

  const handleClose = () => setVideoClip(null);

  const { prefix, suffix } = getMatchPrefixAndSuffix(match);
  const href = withQueryParamsUrl(ROUTES.meetingReportById(sessionId), {
    t: clip?.start,
    // can't send both the search term and the timestamp at the same time due to
    // unresolved issues on the Meeting Report with multiple elements trying to
    // auto-scroll to themselves (see ENG-7073)
    search: clip?.start ? undefined : query,
  });

  return (
    <>
      <Row spacing={0.5}>
        {clip && (
          <>
            <PlayClip
              duration={clip.duration}
              sx={{ mt: "1px" }}
              onClick={() => setVideoClip(clip)}
            />
            <VideoDialog
              id={`${sessionId}-video`}
              recordingInfo={recordingInfo?.recordingInfo as VideoRecording}
              clip={videoClip}
              open={videoClip != null}
              onClose={handleClose}
            />
          </>
        )}
        <ResultMatchText href={href}>
          {match.label && <Bold>{match.label}: </Bold>}
          {prefix}
          <TextWithMatch match={match} />
          {suffix}
        </ResultMatchText>
      </Row>
    </>
  );
};

const SearchResult = memo(
  ({ query, recordingInfo, result }: SearchResultProps) => {
    const meetingReportUrl = withQueryParamsUrl(
      ROUTES.meetingReportById(result.sessionId),
      { search: query }
    );

    const formattedStartTime = result.startDate.toLocaleString({
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const [titleMatches, otherMatches] = partition(
      (m) => m.category === "title",
      result.matches
    );

    const titleMatch = first(titleMatches);
    const matchesToShow = otherMatches.slice(0, MAX_VISIBLE_MATCHES);

    return (
      <Wrapper>
        <Result spacing={0.5}>
          <Link href={meetingReportUrl}>
            <Column mr={1}>
              <Thumbnail src={result.thumbnailSrc} />
            </Column>
          </Link>
          <Column spacing={0.5}>
            <Column spacing={0.25}>
              <Link href={meetingReportUrl}>
                <MeetingTitle>
                  {titleMatch ? (
                    <TextWithMatch match={titleMatch} />
                  ) : (
                    result.title
                  )}
                </MeetingTitle>
              </Link>
              <Row spacing="6px" variant="left">
                <CalendarIcon
                  sx={{ fontSize: "16px", color: "shades.neutral.400" }}
                />
                <SubText>{formattedStartTime}</SubText>
                {(result.ownerName || result.ownerIsCurrentUser) && (
                  <Row sx={{ display: { xxs: "none", sm: "contents" } }}>
                    <SubText>•</SubText>
                    <SubText>
                      {result.ownerIsCurrentUser
                        ? "You're the report owner"
                        : `${result.ownerName} (owner)`}
                    </SubText>
                  </Row>
                )}
                <SubText>•</SubText>
                <SubText sx={{ color: "shades.primary.500" }}>
                  {pluralizePhrase(result.numMatches, "instance")}
                </SubText>
              </Row>
            </Column>
            {isEmpty(otherMatches) ? (
              isEmpty(result.summary) ? null : (
                <ResultMatchText href={meetingReportUrl}>
                  <Bold>Summary:</Bold> {result.summary}
                </ResultMatchText>
              )
            ) : (
              matchesToShow.map((match) => (
                <Match
                  key={match.text}
                  match={match}
                  query={query}
                  recordingInfo={recordingInfo}
                  sessionId={result.sessionId}
                />
              ))
            )}
          </Column>
        </Result>
      </Wrapper>
    );
  },
  (props, prevProps) => {
    return (
      props.recordingInfo?.recordingInfo?.sessionId ===
        prevProps.recordingInfo?.recordingInfo?.sessionId &&
      props.query === prevProps.query &&
      props.result.matches === prevProps.result.matches
    );
  }
);

SearchResult.displayName = "SearchResult";

export default SearchResult;
